import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 7 — shared TanStack Query hooks for the client shell.
 * Every query key below includes workspaceId/member/scope/viewMode so a
 * rapid Workspace switch can never serve stale cached data from the
 * previous Workspace (no shared/ambiguous key across Workspaces).
 */
export function useMe() {
  return useQuery({
    queryKey: ["rehearsal-room", "me"],
    queryFn: async () => {
      const res = await rrFetch("/api/auth/me");
      if (!res.ok) throw new Error("unauthenticated");
      return res.json();
    },
    retry: false,
  });
}

export function viewModeForPortalRole(portalRole) {
  return portalRole === "coach_admin" ? "coach" : "client_member";
}

export function useUpdateMyTimezone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (timezone) => {
      const res = await rrFetch("/api/auth/me/timezone", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ timezone }) });
      if (!res.ok) throw new Error("failed to update timezone");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["rehearsal-room", "me"] }),
  });
}

export function useWorkspaceHome(workspaceId) {
  const meQuery = useMe();
  const { data: me, isError: meError } = meQuery;
  const viewMode = me ? viewModeForPortalRole(me.portal_role) : undefined;
  const homeQuery = useQuery({
    queryKey: ["rehearsal-room", "home", workspaceId, me?.per_id, "self", viewMode],
    enabled: !!workspaceId && !!me,
    queryFn: async () => {
      const res = await rrFetch(`/api/rehearsal-room/workspaces/${workspaceId}/home`);
      if (res.status === 401) throw new Error("unauthenticated");
      if (res.status === 404) throw new Error("not-found");
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
    retry: false,
  });
  // meQuery can 401 while staying permanently disabled/idle (never
  // entering its own error state) — surface it here so route components
  // relying on `isError` to redirect never hang forever on a dead token.
  return { ...homeQuery, isError: homeQuery.isError || meError };
}

export function useWorkspacePracticeLayer(workspaceId, layerKey) {
  const meQuery = useMe();
  const { data: me, isError: meError } = meQuery;
  const layerQuery = useQuery({
    queryKey: ["rehearsal-room", "practice-layer", workspaceId, layerKey, me?.per_id, "self"],
    enabled: !!workspaceId && !!layerKey && !!me,
    queryFn: async () => {
      const res = await rrFetch(`/api/rehearsal-room/workspaces/${workspaceId}/practice/${layerKey}`);
      if (res.status === 401) throw new Error("unauthenticated");
      if (res.status === 404) throw new Error("not-found");
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
    retry: false,
  });
  return { ...layerQuery, isError: layerQuery.isError || meError };
}
